import { useState, useEffect } from 'react'
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useTheme,
  useMediaQuery,
  Alert,
  Box,
  Typography,
  Paper,
} from '@mui/material'
// Import our styled components
import {
  PrimaryButton,
  FormContainer,
  SectionHeader,
} from './ui/StyledComponents'
import DateSelector from './DateSelector'
import AttendanceStatusSelect from './AttendanceStatusSelect'
import ClassSelector from './ClassSelector'
import SnackbarMessage from './SnackbarMessage'
import { useNavigate } from 'react-router-dom'
import supabase from '../supabaseClient'
import dayjs from 'dayjs'
import { useAuth } from '../context/AuthContext'
import { updateCurrentTeacherActivity } from '../utils/teacherActivity'
import { getAttendancePermissions } from '../utils/permissions'
import { getTeacherAccessibleClasses } from '../utils/classUtils'

function ClassAttendanceForm() {
  const [selectedClass, setSelectedClass] = useState('')
  const [students, setStudents] = useState([])
  const [attendanceData, setAttendanceData] = useState([])
  const [progressData, setProgressData] = useState([])
  const [date, setDate] = useState(dayjs(new Date()))
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState('success') // success or error
  const [permissions, setPermissions] = useState(null)
  const [loading, setLoading] = useState(false)
  const [hasNoClasses, setHasNoClasses] = useState(false)

  const navigate = useNavigate()
  const { user, userRole } = useAuth()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // Fetch teacher's accessible classes and handle localStorage
  useEffect(() => {
    const fetchTeacherClasses = async () => {
      if (userRole === 'admin' || userRole === 'super_admin') {
        // Admins have access to all classes, so skip this check
        setHasNoClasses(false)
        // Load stored class for admins
        const storedClass = localStorage.getItem('selectedClass')
        if (storedClass) {
          setSelectedClass(storedClass)
        }
        return
      }

      if (user?.id) {
        try {
          const classes = await getTeacherAccessibleClasses(user.id, 'id')
          const noClasses = classes.length === 0
          setHasNoClasses(noClasses)
          
          if (noClasses) {
            // Teacher has no classes, clear any selected class
            setSelectedClass('')
            localStorage.removeItem('selectedClass')
          } else {
            // Teacher has classes - check if stored class is accessible
            const storedClass = localStorage.getItem('selectedClass')
            if (storedClass) {
              const hasAccess = classes.some(cls => cls.id === storedClass)
              if (hasAccess) {
                setSelectedClass(storedClass)
              } else {
                // Stored class is not accessible, clear it
                setSelectedClass('')
                localStorage.removeItem('selectedClass')
              }
            }
          }
        } catch (error) {
          console.error('Error fetching teacher classes:', error)
          setHasNoClasses(false)
        }
      }
    }

    fetchTeacherClasses()
  }, [user?.id, userRole])

  // Store selected class in localStorage when it changes
  useEffect(() => {
    if (selectedClass) {
      localStorage.setItem('selectedClass', selectedClass)
    }
  }, [selectedClass])

  // Helper function to get attendance status colors based on theme
  const getAttendanceRowColor = (status) => {
    const isDark = theme.palette.mode === 'dark'
    
    switch (status) {
      case 'Absent':
        return isDark ? '#4a1e1e' : '#ffebee' // Dark red for dark mode, light red for light mode
      case 'Excused Absence':
        return isDark ? '#1e4a1e' : '#e8f5e8' // Dark green for dark mode, light green for light mode
      case 'Late':
        return isDark ? '#7c4a1a' : '#fff9c4' // Bright amber for dark mode, light yellow for light mode
      default:
        return 'inherit'
    }
  }

  // Check permissions and get effective teacher when class or date changes
  useEffect(() => {
    if (selectedClass && user) {
      const checkPermissions = async () => {
        setLoading(true)
        try {
          const dateStr = date.format('YYYY-MM-DD')
          const perms = await getAttendancePermissions(user.id, selectedClass, dateStr, userRole)
          setPermissions(perms)
          
          // If user doesn't have permission and isn't an admin, redirect to attendance page
          if (!perms.canView && userRole !== 'admin' && userRole !== 'super_admin') {
            setSnackbarMessage(`Access denied: ${perms.reason}. Redirecting to attendance page...`)
            setSnackbarSeverity('warning')
            setOpenSnackbar(true)
            
            // Redirect after showing the message
            setTimeout(() => {
              navigate('/teacher/' + user.id + '/attendance')
            }, 2000)
          }
        } catch (error) {
          console.error('Error checking permissions:', error)
          setPermissions({
            canCreate: false,
            canEdit: false,
            canDelete: false,
            canView: false,
            reason: 'Error checking permissions'
          })
        } finally {
          setLoading(false)
        }
      }
      checkPermissions()
    }
  }, [selectedClass, date, user, userRole, navigate])

  useEffect(() => {
    if (selectedClass && permissions?.canView) {
      const fetchStudentsAndAttendance = async () => {
        // Fetch students for the selected class (only active students)
        const { data: studentsData, error: studentsError } = await supabase
          .from('students')
          .select('*')
          .contains('class_ids', [selectedClass])
          .eq('is_active', true)

        if (studentsError) {
          console.error('Error fetching students:', studentsError)
        } else {
          const [attendanceRecords, progressRecords] = await Promise.all([
            Promise.all(
              studentsData.map(async (student) => {
                const { data: attendance, error } = await supabase
                  .from('attendance')
                  .select('status')
                  .eq('student_id', student.id)
                  .eq('class_id', selectedClass)
                  .eq('date', date.format('YYYY-MM-DD'))
                  .maybeSingle()

                if (error || !attendance) {
                  return { student_id: student.id, status: 'Present' }
                } else {
                  return { student_id: student.id, status: attendance.status }
                }
              }),
            ),
            Promise.all(
              studentsData.map(async (student) => {
                const [quranProgress, greenbookProgress] = await Promise.all([
                  supabase
                    .from('student_quran_progress')
                    .select('*')
                    .eq('student_id', student.id)
                    .eq('class_id', selectedClass)
                    .eq('date', date.format('YYYY-MM-DD'))
                    .maybeSingle(),
                  supabase
                    .from('student_greenbook_progress')
                    .select('*')
                    .eq('student_id', student.id)
                    .eq('class_id', selectedClass)
                    .eq('date', date.format('YYYY-MM-DD'))
                    .maybeSingle()
                ])

                return {
                  student_id: student.id,
                  hasQuranProgress: !quranProgress.error && quranProgress.data !== null,
                  quranStatus: quranProgress.data?.assignment_status,
                  hasGreenbookProgress: !greenbookProgress.error && greenbookProgress.data !== null,
                  isGreenbookCompleted: !greenbookProgress.error && greenbookProgress.data !== null && greenbookProgress.data.assignment_completed,
                }
              })
            )
          ])
          setStudents(studentsData)
          setAttendanceData(attendanceRecords)
          setProgressData(progressRecords)
        }
      }
      fetchStudentsAndAttendance()
    }
  }, [selectedClass, permissions, date])

  const handleStatusChange = (studentId, status) => {
    const updatedAttendance = attendanceData.map((attendance) =>
      attendance.student_id === studentId
        ? { ...attendance, status }
        : attendance,
    )
    setAttendanceData(updatedAttendance)
  }

  const saveAttendance = async () => {
    if (!selectedClass) {
      setSnackbarMessage('Please select a class.')
      setSnackbarSeverity('error')
      setOpenSnackbar(true)
      return
    }

    if (!date) {
      setSnackbarMessage('Please select a date.')
      setSnackbarSeverity('error')
      setOpenSnackbar(true)
      return
    }

    if (students.length === 0) {
      setSnackbarMessage('There are no students in the selected class.')
      setSnackbarSeverity('error')
      setOpenSnackbar(true)
      return
    }

    if (!permissions?.canCreate) {
      setSnackbarMessage(`You don't have permission to record attendance. ${permissions?.reason || ''}`)
      setSnackbarSeverity('error')
      setOpenSnackbar(true)
      return
    }

    const formattedDate = date.format('YYYY-MM-DD')

    const attendanceRecords = attendanceData.map(({ student_id, status }) => ({
      student_id,
      class_id: selectedClass,
      date: formattedDate,
      status,
    }))

    const { data, error } = await supabase
      .from('attendance')
      .upsert(attendanceRecords, {
        onConflict: ['student_id', 'date', 'class_id'],
      })

    if (error) {
      console.error('Error saving attendance:', error)
      setSnackbarMessage('Error saving attendance.')
      setSnackbarSeverity('error')
      setOpenSnackbar(true)
    } else {
      console.log('Attendance saved or updated:', data)
      setSnackbarMessage('Attendance saved successfully!')
      setSnackbarSeverity('success')
      setOpenSnackbar(true)
      
      // Update teacher activity tracking
      await updateCurrentTeacherActivity(user, userRole)
    }
  }

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false)
  }

  const handleRowClick = (studentId, classId) => {
    navigate(`/student/${studentId}/class/${classId}?date=${date.format('YYYY-MM-DD')}`)
  }

  return (
    <FormContainer>
      <Grid container spacing={{ xs: 2, sm: 2 }}>
        <Grid item xs={12}>
          <SectionHeader sx={{ 
            mb: { xs: 1.5, sm: 2 },
            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
          }}>
            Class Attendance
          </SectionHeader>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'stretch', sm: 'center' }, 
            mb: { xs: 1.5, sm: 2 },
            gap: { xs: 2, sm: 0 },
          }}>
            <Box sx={{ 
              minWidth: { xs: '100%', sm: 200 },
              width: { xs: '100%', sm: 200 },
            }}>
              <ClassSelector
                selectedClass={selectedClass}
                setSelectedClass={setSelectedClass}
              />
            </Box>
            <Box sx={{ 
              minWidth: { xs: '100%', sm: 200 },
              width: { xs: '100%', sm: 200 },
            }}>
              <DateSelector
                value={date}
                onChange={(newDate) => setDate(newDate)}
              />
            </Box>
          </Box>
        </Grid>

        {selectedClass ? (
          <>

            {/* Permissions Alert */}
            {permissions && !permissions.canView && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  {hasNoClasses 
                    ? "You don't have any classes assigned. Please contact an administrator if you believe this is an error."
                    : `You don't have permission to view attendance for this class. ${permissions.reason}`
                  }
                </Alert>
              </Grid>
            )}

            {permissions && permissions.canView && !permissions.canCreate && (
              <Grid item xs={12}>
                <Alert severity="info">
                  You can view attendance but cannot make changes. {permissions.reason}
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <TableContainer 
                component={Paper}
                sx={{ 
                  overflowX: { xs: 'auto', sm: 'visible' },
                  '&::-webkit-scrollbar': {
                    height: { xs: '8px', sm: '0px' },
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '4px',
                  },
                }}
              >
                <Table sx={{ minWidth: { xs: 600, sm: 'auto' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        width: { xs: '40px', sm: '50px' },
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}>
                        #
                      </TableCell>
                      <TableCell sx={{ 
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}>
                        First Name
                      </TableCell>
                      <TableCell sx={{ 
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}>
                        Last Name
                      </TableCell>
                      <TableCell sx={{ 
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}>
                        Status
                      </TableCell>
                      <TableCell sx={{ 
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}>
                        Progress
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {students.length > 0 ? (
                      students.map((student, index) => {
                        const studentProgress = progressData.find(
                          (p) => p.student_id === student.id
                        )
                        return (
                          <TableRow
                            key={student.id}
                            onClick={() =>
                              handleRowClick(student.id, selectedClass)
                            }
                            style={{ 
                              cursor: 'pointer',
                              backgroundColor: getAttendanceRowColor(
                                attendanceData.find(
                                  (a) => a.student_id === student.id
                                )?.status
                              )
                            }}
                          >
                            <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              {index + 1}
                            </TableCell>
                            <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              {student.first_name}
                            </TableCell>
                            <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              {student.last_name}
                            </TableCell>
                            <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              <AttendanceStatusSelect
                                value={
                                  attendanceData.find(
                                    (a) => a.student_id === student.id,
                                  )?.status || 'Present'
                                }
                                onChange={(e) =>
                                  handleStatusChange(student.id, e.target.value)
                                }
                                onClick={(e) => e.stopPropagation()}
                                disabled={!permissions?.canCreate}
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap' }}>
                                {student.is_quran_student && (
                                  <Chip
                                    label="Quran"
                                    size="small"
                                    color={
                                      !studentProgress?.hasQuranProgress 
                                        ? "default" 
                                        : !studentProgress?.quranStatus  // if status is null
                                          ? "default"
                                          : studentProgress?.quranStatus === 'full'
                                            ? "success"
                                            : studentProgress?.quranStatus === 'partial'
                                              ? "warning"
                                              : studentProgress?.quranStatus === 'none'
                                                ? "error"
                                                : "default"
                                    }
                                    variant={studentProgress?.hasQuranProgress && studentProgress?.quranStatus ? "filled" : "outlined"}
                                    sx={{ 
                                      fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                      height: { xs: '20px', sm: '24px' },
                                    }}
                                  />
                                )}
                                {student.is_greenbook_student && (
                                  <Chip
                                    label="Greenbook"
                                    size="small"
                                    color={!studentProgress?.hasGreenbookProgress ? "default" : studentProgress?.isGreenbookCompleted ? "success" : "error"}
                                    variant={studentProgress?.hasGreenbookProgress ? "filled" : "outlined"}
                                    sx={{ 
                                      fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                      height: { xs: '20px', sm: '24px' },
                                    }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        )
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: { xs: 3, sm: 4 } }}>
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            No students in this class
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            {permissions?.canCreate && (
              <Grid item xs={12}>
                <PrimaryButton 
                  variant="contained" 
                  onClick={saveAttendance}
                  disabled={loading}
                  fullWidth={isMobile}
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    padding: { xs: '10px 16px', sm: '12px 24px' },
                  }}
                >
                  Submit Attendance
                </PrimaryButton>
              </Grid>
            )}
          </>
        ) : (
          <Grid item xs={12}>
            <Box sx={{ 
              textAlign: 'center', 
              py: { xs: 3, sm: 4 },
              color: 'text.secondary'
            }}>
              {hasNoClasses ? (
                <>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    No Classes Assigned
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    You don't have any classes assigned. Please contact an administrator if you believe this is an error.
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    📚 Select a Class
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    Choose a class from the dropdown above to view and manage attendance
                  </Typography>
                </>
              )}
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Snackbar for success or error */}
      <SnackbarMessage
        open={openSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleCloseSnackbar}
      />
    </FormContainer>
  )
}

export default ClassAttendanceForm
